<template>
  <div class="cr-page txn-page">
    <SnackbarComp :SnackbarComponent="SnackbarComponent" />
    <RequestCreditsDialog
      v-model="showRequestCreditsDialog"
      @saved="onCreditRequestSaved"
      @error="showCreditRequestError"
    />

    <div class="cr-panel">
      <div class="cr-panel__toolbar txn-panel__toolbar">
        <div class="cr-panel__toolbar-start">
          <div class="txn-panel__filters">
            <v-select
              v-model="filterTransactionType"
              :items="transactionTypeOptions"
              item-title="label"
              item-value="value"
              density="compact"
              variant="outlined"
              hide-details
              class="txn-filter-select"
              rounded="lg"
              :disabled="!isSessionReady || loadingTransactions"
              @update:model-value="onFilterChange"
            />
            <v-select
              v-model="filterDeductionType"
              :items="deductionTypeOptions"
              item-title="label"
              item-value="value"
              density="compact"
              variant="outlined"
              hide-details
              class="txn-filter-select"
              rounded="lg"
              :disabled="!isSessionReady || loadingTransactions"
              @update:model-value="onFilterChange"
            />
          </div>
          <span class="cr-panel__count">
            {{ filteredTransactions.length }} transaction{{ filteredTransactions.length === 1 ? '' : 's' }}
          </span>
        </div>
        <TransactionsHeader
          :credits-balance="creditsBalanceDisplay"
          :total-transactions="filteredTransactions.length"
          :loading="loadingTransactions"
          :is-session-ready="isSessionReady"
          :is-search-active="isSearchActive"
          @refresh="loadTransactions"
        />
      </div>

      <TransactionsList
        :transactions="paginatedTransactions"
        :loading="loadingTransactions"
        :is-search-active="isSearchActive"
        :organization="organization"
      />

      <PortalListPagination
        class="cr-panel__footer"
        :page="page"
        :page-count="pageCount"
        :items-per-page="itemsPerPage"
        :range-label="rangeLabel"
        :total="filteredTransactions.length"
        @update:page="setPage"
        @update:items-per-page="setItemsPerPage"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import listOrgTransactions from '@/mixins/Transactions/listOrgTransactions'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import PortalListPagination from '@/components/Extras/PortalListPagination.vue'
import RequestCreditsDialog from '@/components/Credits/RequestCreditsDialog.vue'
import TransactionsHeader from '@/components/Transactions/TransactionsHeader.vue'
import TransactionsList from '@/components/Transactions/TransactionsList.vue'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList } from '@/utils/graphql'
import { normalizeTransactionRecord } from '@/utils/session'

const TRANSACTION_SEARCH_FIELDS = [
  'transactionType',
  'deductionType',
  'amount',
  'duration_seconds',
  'balanceAfter',
  'description',
  'createdOn',
]

export default {
  name: 'TransactionsPage',
  components: {
    SnackbarComp,
    PortalListPagination,
    RequestCreditsDialog,
    TransactionsHeader,
    TransactionsList,
  },
  mixins: [listOrgTransactions],
  data() {
    return {
      SnackbarComponent: {},
      showRequestCreditsDialog: false,
      transactions: [],
      loadingTransactions: false,
      page: 1,
      itemsPerPage: 25,
      filterTransactionType: null,
      filterDeductionType: null,
      transactionTypeOptions: [
        { label: 'All types', value: null },
        { label: 'Credit', value: 'CREDIT' },
        { label: 'Debit', value: 'DEBIT' },
      ],
      deductionTypeOptions: [
        { label: 'All categories', value: null },
        { label: 'Topup', value: 'TOP_UP' },
        { label: 'Call', value: 'CALL' },
        { label: 'Whatsapp Log', value: 'WHATSAPP_LOG' },
        { label: 'Web call', value: 'WEB_CALL' },
        { label: 'Daily rental', value: 'DAILY_RENTAL' },
      ],
    }
  },
  computed: {
    ...mapState(['tableSearch', 'organization']),
    ...mapGetters(['isSessionReady', 'sessionContext']),
    filteredTransactions() {
      const q = (this.tableSearch || '').trim().toLowerCase()
      if (!q) return this.transactions
      return this.transactions.filter((row) =>
        TRANSACTION_SEARCH_FIELDS.some((field) =>
          String(row[field] ?? '')
            .toLowerCase()
            .includes(q),
        ),
      )
    },
    pageCount() {
      if (this.itemsPerPage <= 0) return 1
      return Math.max(1, Math.ceil(this.filteredTransactions.length / this.itemsPerPage))
    },
    paginatedTransactions() {
      if (this.itemsPerPage <= 0) return this.filteredTransactions
      const start = (this.page - 1) * this.itemsPerPage
      return this.filteredTransactions.slice(start, start + this.itemsPerPage)
    },
    rangeLabel() {
      const total = this.filteredTransactions.length
      if (!total) return '0 of 0'
      if (this.itemsPerPage <= 0) return `1-${total} of ${total}`
      const start = (this.page - 1) * this.itemsPerPage + 1
      const end = Math.min(this.page * this.itemsPerPage, total)
      return `${start}-${end} of ${total}`
    },
    creditsBalanceDisplay() {
      const balance = this.organization?.credits_balance
      if (balance == null || balance === '') return '—'
      const n = Number(balance)
      if (!Number.isFinite(n)) return String(balance)
      return Math.round(n).toLocaleString()
    },
    isSearchActive() {
      return Boolean((this.tableSearch || '').trim())
    },
  },
  watch: {
    isSessionReady: {
      immediate: true,
      handler(ready) {
        if (!ready) return
        this.loadTransactions()
      },
    },
    '$route.name'(name) {
      if (name === 'Transactions' && this.isSessionReady) {
        this.loadTransactions()
      }
    },
    filteredTransactions() {
      if (this.page > this.pageCount) {
        this.page = this.pageCount
      }
    },
    tableSearch() {
      this.page = 1
    },
  },
  methods: {
    transactionRowKey(item) {
      return item.id || `${item.createdOn}-${item.amount}-${item.transactionType}`
    },
    setPage(next) {
      this.page = Math.min(Math.max(1, next), this.pageCount)
    },
    setItemsPerPage(next) {
      this.itemsPerPage = next
      this.page = 1
    },
    onFilterChange() {
      this.page = 1
      this.loadTransactions()
    },
    showError(payload, fallback) {
      this.SnackbarComponent = {
        SnackbarVmodel: true,
        SnackbarColor: 'error',
        SnackbarText: getApiMessage(payload, fallback),
      }
    },
    showSuccess(message) {
      this.SnackbarComponent = {
        SnackbarVmodel: true,
        SnackbarColor: 'success',
        SnackbarText: message,
      }
    },
    showCreditRequestError(payload) {
      this.showError(payload, 'Recharge failed')
    },
    onCreditRequestSaved() {
      this.showSuccess(
        'Payment successful. Credits will be added after verification.',
      )
      this.loadTransactions()
    },
    buildTransactionListInput() {
      const { org_id } = this.sessionContext ?? {}
      const input = { org_id }
      if (this.filterTransactionType) {
        input.transaction_type = this.filterTransactionType
      }
      if (this.filterDeductionType) {
        input.deduction_type = this.filterDeductionType
      }
      return input
    },
    normalizeTransactions(raw) {
      const list = unwrapApiList(raw)
      return list
        .map(normalizeTransactionRecord)
        .filter(Boolean)
        .map((row, idx) => ({
          ...row,
          id: row.id || `txn-${idx}`,
        }))
    },
    async loadTransactions() {
      const { org_id } = this.sessionContext ?? {}
      if (!this.isSessionReady || !org_id) return

      this.loadingTransactions = true
      try {
        const response = await this.listOrgTransactions(this.buildTransactionListInput())
        const rows = this.normalizeTransactions(response)

        if (rows.length) {
          this.transactions = rows
          this.page = 1
          return
        }

        if (!isApiSuccess(response)) {
          this.showError(response, 'Failed to load transactions')
          this.transactions = []
          return
        }

        this.transactions = rows
        this.page = 1
      } catch (err) {
        this.showError(err, 'Failed to load transactions')
        this.transactions = []
      } finally {
        this.loadingTransactions = false
      }
    },
  },
}
</script>
